import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Image, Link2, Video, RefreshCw } from 'lucide-react';

const ContentEditor = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/content`);
      const data = await response.json();
      setContent(data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(`${backendUrl}/api/admin/content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(content)
      });

      if (response.ok) {
        setMessage('Content saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setMessage('Error saving content');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const updateSocialLink = (platform, url) => {
    setContent(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: url }
    }));
  };

  const addMomentsImage = () => {
    setContent(prev => ({
      ...prev,
      moments_images: [...(prev.moments_images || []), { id: Date.now(), image: '' }]
    }));
  };

  const updateMomentsImage = (index, url) => {
    const updated = [...(content.moments_images || [])];
    updated[index] = { ...updated[index], image: url };
    setContent(prev => ({ ...prev, moments_images: updated }));
  };

  const removeMomentsImage = (index) => {
    const updated = (content.moments_images || []).filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, moments_images: updated }));
  };

  const addSipVideo = () => {
    setContent(prev => ({
      ...prev,
      sip_videos: [...(prev.sip_videos || []), { id: Date.now(), title: '', url: '' }]
    }));
  };

  const updateSipVideo = (index, field, value) => {
    const updated = [...(content.sip_videos || [])];
    updated[index] = { ...updated[index], [field]: value };
    setContent(prev => ({ ...prev, sip_videos: updated }));
  };

  const removeSipVideo = (index) => {
    const updated = (content.sip_videos || []).filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, sip_videos: updated }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Save Button */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-stone-900">Edit Site Content</h2>
        <div className="flex items-center gap-3">
          {message && (
            <span className={`text-sm ${message.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>
              {message}
            </span>
          )}
          <button
            onClick={saveContent}
            disabled={saving}
            className="bg-teal-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-teal-600 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Basic Info */}
        <section className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-stone-900 mb-4">Basic Info</h3>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm text-stone-600 mb-1">Brand Name</label>
              <input
                type="text"
                value={content?.brand_name || ''}
                onChange={(e) => updateField('brand_name', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm text-stone-600 mb-1">Location</label>
              <input
                type="text"
                value={content?.location || ''}
                onChange={(e) => updateField('location', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm text-stone-600 mb-1">Tagline</label>
              <input
                type="text"
                value={content?.tagline || ''}
                onChange={(e) => updateField('tagline', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2"
              />
            </div>
          </div>
        </section>

        {/* Signature */}
        <section className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-stone-900 mb-4">Signature Section</h3>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm text-stone-600 mb-1">Statement</label>
              <input
                type="text"
                value={content?.signature_statement || ''}
                onChange={(e) => updateField('signature_statement', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm text-stone-600 mb-1">Subtext</label>
              <input
                type="text"
                value={content?.signature_subtext || ''}
                onChange={(e) => updateField('signature_subtext', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2"
              />
            </div>
          </div>
        </section>

        {/* Profile Images */}
        <section className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Image className="w-5 h-5" />
            Profile Images
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-stone-600 mb-1">Naomi (IRL) Image URL</label>
              <input
                type="text"
                value={content?.naomi_image || ''}
                onChange={(e) => updateField('naomi_image', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2"
                placeholder="https://..."
              />
              {content?.naomi_image && (
                <img src={content.naomi_image} alt="Preview" className="mt-2 w-20 h-28 object-cover rounded" />
              )}
            </div>
            <div>
              <label className="block text-sm text-stone-600 mb-1">NAE-I (URL) Image URL</label>
              <input
                type="text"
                value={content?.naei_image || ''}
                onChange={(e) => updateField('naei_image', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2"
                placeholder="https://..."
              />
              {content?.naei_image && (
                <img src={content.naei_image} alt="Preview" className="mt-2 w-20 h-28 object-cover rounded" />
              )}
            </div>
          </div>
        </section>

        {/* Social Links */}
        <section className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Social Links
          </h3>
          <div className="grid gap-4">
            {['instagram', 'linkedin', 'youtube', 'email'].map((platform) => (
              <div key={platform}>
                <label className="block text-sm text-stone-600 mb-1 capitalize">{platform}</label>
                <input
                  type="text"
                  value={content?.social_links?.[platform] || ''}
                  onChange={(e) => updateSocialLink(platform, e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2"
                  placeholder={platform === 'email' ? 'mailto:...' : 'https://...'}
                />
              </div>
            ))}
          </div>
        </section>

        {/* SIP Videos */}
        <section className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Video className="w-5 h-5" />
            SIP Sessions Videos
          </h3>
          <div className="space-y-4">
            {(content?.sip_videos || []).map((video, index) => (
              <div key={video.id || index} className="flex gap-3 items-start bg-stone-50 p-3 rounded-lg">
                <div className="flex-1 grid md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={video.title || ''}
                    onChange={(e) => updateSipVideo(index, 'title', e.target.value)}
                    className="border border-stone-200 rounded-lg px-3 py-2"
                    placeholder="Video title"
                  />
                  <input
                    type="text"
                    value={video.url || ''}
                    onChange={(e) => updateSipVideo(index, 'url', e.target.value)}
                    className="border border-stone-200 rounded-lg px-3 py-2"
                    placeholder="YouTube URL"
                  />
                </div>
                <button
                  onClick={() => removeSipVideo(index)}
                  className="text-red-500 hover:text-red-700 p-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={addSipVideo}
              className="flex items-center gap-2 text-teal-600 hover:text-teal-700"
            >
              <Plus className="w-4 h-4" />
              Add Video
            </button>
          </div>
        </section>

        {/* Moments Images */}
        <section className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Image className="w-5 h-5" />
            Moments Gallery
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {(content?.moments_images || []).map((img, index) => (
              <div key={img.id || index} className="relative group">
                <input
                  type="text"
                  value={img.image || ''}
                  onChange={(e) => updateMomentsImage(index, e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-xs"
                  placeholder="Image URL"
                />
                {img.image && (
                  <img src={img.image} alt="" className="mt-2 w-full h-24 object-cover rounded" />
                )}
                <button
                  onClick={() => removeMomentsImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={addMomentsImage}
            className="mt-4 flex items-center gap-2 text-teal-600 hover:text-teal-700"
          >
            <Plus className="w-4 h-4" />
            Add Image
          </button>
        </section>

        {/* Community */}
        <section className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-stone-900 mb-4">Community Section</h3>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm text-stone-600 mb-1">Text</label>
              <textarea
                value={content?.community_text || ''}
                onChange={(e) => updateField('community_text', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 h-20"
              />
            </div>
            <div>
              <label className="block text-sm text-stone-600 mb-1">Button Text</label>
              <input
                type="text"
                value={content?.community_button_text || ''}
                onChange={(e) => updateField('community_button_text', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm text-stone-600 mb-1">Community Link URL</label>
              <input
                type="text"
                value={content?.community_link || ''}
                onChange={(e) => updateField('community_link', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2"
                placeholder="https://airtable.com/..."
              />
            </div>
          </div>
        </section>

        {/* CTA Links */}
        <section className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-stone-900 mb-4">CTA Links</h3>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm text-stone-600 mb-1">Work With Me Link</label>
              <input
                type="text"
                value={content?.work_with_me_link || ''}
                onChange={(e) => updateField('work_with_me_link', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2"
                placeholder="https://airtable.com/..."
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContentEditor;
